import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';

// Magnetic Button - attracts slightly towards cursor on hover
export default function MagneticButton({ children, className, style, ...props }) {
  const buttonRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    // Check if mobile - disable on mobile
    const isMobile = window.innerWidth < 768 || 'ontouchstart' in window;
    if (isMobile) return;

    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      // Calculate offset from center (max 6px movement)
      const dx = (e.clientX - centerX) * 0.25;
      const dy = (e.clientY - centerY) * 0.25;
      
      // Clamp to max 6px
      const maxOffset = 6;
      const clampedX = Math.max(-maxOffset, Math.min(maxOffset, dx));
      const clampedY = Math.max(-maxOffset, Math.min(maxOffset, dy));

      // Animate using anime.js
      if (animRef.current) animRef.current.pause();
      animRef.current = anime({
        targets: button,
        translateX: clampedX,
        translateY: clampedY,
        duration: 200,
        easing: 'easeOutElastic(1, 0.6)',
      });
    };

    const handleMouseLeave = () => {
      // Return to center with spring effect
      if (animRef.current) animRef.current.pause();
      animRef.current = anime({
        targets: button,
        translateX: 0,
        translateY: 0,
        duration: 400,
        easing: 'easeOutElastic(1, 0.5)',
      });
    };

    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <button 
      ref={buttonRef} 
      className={className} 
      style={{ ...style, willChange: 'transform' }}
      {...props}
    >
      {children}
    </button>
  );
}

// Router Link version for react-router-dom <Link>
export function MagneticRouterLink({ children, className, style, to, ...props }) {
  const linkRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const link = linkRef.current;
    if (!link) return;

    const isMobile = window.innerWidth < 768 || 'ontouchstart' in window;
    if (isMobile) return;

    const handleMouseMove = (e) => {
      const rect = link.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dx = (e.clientX - centerX) * 0.25;
      const dy = (e.clientY - centerY) * 0.25;
      const maxOffset = 6;
      const clampedX = Math.max(-maxOffset, Math.min(maxOffset, dx));
      const clampedY = Math.max(-maxOffset, Math.min(maxOffset, dy));
      if (animRef.current) animRef.current.pause();
      animRef.current = anime({
        targets: link,
        translateX: clampedX,
        translateY: clampedY,
        duration: 200,
        easing: 'easeOutElastic(1, 0.6)',
      });
    };

    const handleMouseLeave = () => {
      if (animRef.current) animRef.current.pause();
      animRef.current = anime({
        targets: link,
        translateX: 0,
        translateY: 0,
        duration: 400,
        easing: 'easeOutElastic(1, 0.5)',
      });
    };

    link.addEventListener('mousemove', handleMouseMove);
    link.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      link.removeEventListener('mousemove', handleMouseMove);
      link.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <Link
      ref={linkRef}
      to={to}
      className={className}
      style={{ ...style, willChange: 'transform', display: 'inline-block' }}
      {...props}
    >
      {children}
    </Link>
  );
}

// Link version for <a> tags
export function MagneticLink({ children, className, style, href, target, rel, ...props }) {
  const linkRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const link = linkRef.current;
    if (!link) return;

    const isMobile = window.innerWidth < 768 || 'ontouchstart' in window;
    if (isMobile) return;

    const handleMouseMove = (e) => {
      const rect = link.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const dx = (e.clientX - centerX) * 0.25;
      const dy = (e.clientY - centerY) * 0.25;
      
      const maxOffset = 6;
      const clampedX = Math.max(-maxOffset, Math.min(maxOffset, dx));
      const clampedY = Math.max(-maxOffset, Math.min(maxOffset, dy));

      if (animRef.current) animRef.current.pause();
      animRef.current = anime({
        targets: link,
        translateX: clampedX,
        translateY: clampedY,
        duration: 200,
        easing: 'easeOutElastic(1, 0.6)',
      });
    };

    const handleMouseLeave = () => {
      if (animRef.current) animRef.current.pause();
      animRef.current = anime({
        targets: link,
        translateX: 0,
        translateY: 0,
        duration: 400,
        easing: 'easeOutElastic(1, 0.5)',
      });
    };

    link.addEventListener('mousemove', handleMouseMove);
    link.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      link.removeEventListener('mousemove', handleMouseMove);
      link.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <a 
      ref={linkRef} 
      className={className} 
      style={{ ...style, willChange: 'transform', display: 'inline-block' }}
      href={href}
      target={target}
      rel={rel}
      {...props}
    >
      {children}
    </a>
  );
}
